import { useState } from "preact/hooks";
import { locale, t } from "../i18n";
import { Icon } from "../icon";
import * as S from "../state";
import { Avatar, Segmented, duration } from "../widgets";

/** « Aujourd'hui », « Hier », else « lundi 2 novembre » (with the year when not this one) */
function dayOf(at: number): string {
  const d = new Date(at);
  const now = new Date();
  const y = new Date(now);
  y.setDate(now.getDate() - 1);
  if (d.toDateString() === now.toDateString()) return t("history.today");
  if (d.toDateString() === y.toDateString()) return t("history.yesterday");
  const text = d.toLocaleDateString(locale.value, {
    weekday: "long",
    day: "numeric",
    month: "long",
    ...(d.getFullYear() !== now.getFullYear() ? { year: "numeric" } : {}),
  });
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** the time of day only: the day is in the header above */
function timeOf(at: number): string {
  const mins = Math.floor((Date.now() - at) / 60000);
  if (mins < 1) return t("time.just_now");
  if (mins < 60) return t("time.min_ago", mins);
  return new Date(at).toLocaleTimeString(locale.value, { hour: "2-digit", minute: "2-digit" });
}

export function History() {
  const [view, setView] = useState<"all" | "missed">("all");
  const [confirm, setConfirm] = useState(false);
  const all = S.historyRows();
  const missedCount = all.filter((e) => e.missed).length;
  const h = view === "missed" ? all.filter((e) => e.missed) : all;
  const cl = S.callLog.value;
  let lastDay = "";
  return (
    <div class="page">
      <div class="row between">
        <h1>{t("history.title")}</h1>
        {all.length > 0 && (
          <button type="button" class="btn link" onClick={() => setConfirm(true)}>
            {t("history.clear")}
          </button>
        )}
      </div>
      {confirm && (
        <div class="item" role="alertdialog" aria-label={t("history.clear_q")}>
          <div class="grow">
            <div class="title">{t("history.clear_q")}</div>
            <div class="sub">{cl.enabled ? t("history.clear_everywhere") : t("history.clear_here")}</div>
          </div>
          <button type="button" class="btn" onClick={() => setConfirm(false)}>
            {t("common.cancel")}
          </button>
          <button
            type="button"
            class="btn danger"
            onClick={() => {
              setConfirm(false);
              void S.clearHistory();
            }}
          >
            {t("history.clear_yes")}
          </button>
        </div>
      )}
      {all.length > 0 && (
        <Segmented
          label={t("history.title")}
          value={view}
          options={[
            ["all", t("history.all")],
            ["missed", missedCount ? t("history.missed_n", missedCount) : t("history.missed_tab")],
          ]}
          onChange={setView}
        />
      )}
      {h.length === 0 ? (
        <p class="empty">{view === "missed" && all.length ? t("history.none_missed") : t("history.none")}</p>
      ) : (
        <div class="list">
          {h.map((e) => {
            const day = dayOf(e.at);
            const header = day !== lastDay ? day : "";
            lastDay = day;
            const name = e.name === e.number ? S.prettyNumber(e.number) : e.name;
            const what = e.outcome || (e.missed ? t("history.missed") : "");
            const line = S.lines.value.length > 1 && e.account ? S.lineTitle(e.account) : "";
            const parts = [e.duration ? duration(e.duration) : "", what, e.name !== e.number ? S.prettyNumber(e.number) : "", timeOf(e.at), line, e.pending ? t("history.syncing") : ""].filter(Boolean);
            return (
              <>
              {header && <div class="day-header" role="heading" aria-level={2}>{header}</div>}
              <div class="item" data-row={e.id}>
                <Avatar name={name} />
                <div class="grow">
                  <button type="button" class={`title linkish ${e.missed ? "danger-text" : ""}`} onClick={() => S.openPerson(e.number)} title={t("after.open_card")}>
                    {name}
                  </button>
                  <div class="sub row" style={{ gap: "6px" }}>
                    <Icon name={e.missed ? "call-missed" : e.dir === "in" ? "call-in" : "call-out"} size={14} />
                    <span class="outcome">{parts.join(" · ")}</span>
                  </div>
                  {e.clips.length > 0 && <div class="sub clip-note">{e.clips.join(" · ")}</div>}
                </div>
                {cl.enabled && (
                  <button type="button" class="btn link" aria-label={t("history.delete")} title={t("history.delete")} onClick={() => void S.deleteHistoryRow(e)}>
                    <Icon name="trash" size={16} />
                  </button>
                )}
                <button type="button" class="btn success" aria-label={`${t("dialer.call")} ${name}`} title={t("dialer.call")} onClick={() => void S.dial(e.number, e.account)}>
                  <Icon name="phone" size={18} />
                </button>
              </div>
              </>
            );
          })}
          {cl.enabled && cl.older && (
            <button type="button" class="btn link" onClick={() => void S.moreCallLog()}>
              {t("history.more")}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
